/**
 * Namngivna revisioner — punktvisa snapshots av en Y.Doc lagrade i en egen
 * IndexedDB-databas (separat från y-indexeddb:ens docstore så vi kan lista
 * snapshots utan att öppna själva dokumentet). Varje post = hela doc:ens
 * state som Yjs-update-binär + label + timestamp.
 *
 * Vi normaliserar namngivningen till bas64 i JSON-records så listan är
 * serialiserbar och enkel att kopiera mellan browsers via export/import.
 */

const DB_NAME = "electro-planner-revisions";
const STORE = "by-document";
const DB_VERSION = 1;

export type RevisionRecord = {
  id: string;
  label: string;
  createdAt: string;
  sizeBytes: number;
  /** Yjs-update kodad som base64 för enkel serialisering. */
  updateB64: string;
};

type StoredList = RevisionRecord[];

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readList(documentId: string): Promise<StoredList> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(documentId);
    req.onsuccess = () => resolve((req.result as StoredList | undefined) ?? []);
    req.onerror = () => reject(req.error);
  });
}

async function writeList(documentId: string, list: StoredList): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(list, documentId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listRevisions(documentId: string): Promise<RevisionRecord[]> {
  const list = await readList(documentId);
  return [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function saveRevision(
  documentId: string,
  label: string,
  updateBytes: Uint8Array,
): Promise<RevisionRecord> {
  const list = await readList(documentId);
  const record: RevisionRecord = {
    id: makeRevisionId(),
    label: label.trim() || new Date().toLocaleString("sv-SE"),
    createdAt: new Date().toISOString(),
    sizeBytes: updateBytes.byteLength,
    updateB64: encodeB64(updateBytes),
  };
  list.push(record);
  await writeList(documentId, list);
  return record;
}

export async function deleteRevision(
  documentId: string,
  revisionId: string,
): Promise<void> {
  const list = await readList(documentId);
  const next = list.filter((r) => r.id !== revisionId);
  if (next.length === list.length) return;
  await writeList(documentId, next);
}

export async function getRevisionBytes(
  documentId: string,
  revisionId: string,
): Promise<Uint8Array | null> {
  const list = await readList(documentId);
  const rec = list.find((r) => r.id === revisionId);
  if (!rec) return null;
  return decodeB64(rec.updateB64);
}

export async function renameRevision(
  documentId: string,
  revisionId: string,
  label: string,
): Promise<void> {
  const list = await readList(documentId);
  const idx = list.findIndex((r) => r.id === revisionId);
  if (idx < 0) return;
  list[idx] = { ...list[idx]!, label: label.trim() || list[idx]!.label };
  await writeList(documentId, list);
}

function makeRevisionId(): string {
  return `rev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function encodeB64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function decodeB64(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
